import React, { useEffect, useState } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { User, Trash2, Plus, CheckCircle, AlertCircle, Clock } from "lucide-react";
import { countries } from "./CountryName";


const createEmptyChild = () => ({
  firstName: "",
  lastName: "",
  dob: "",
  gender: "",
  education: "",
  currentCountry: "",
  isAccompanying: false
});

const ChildForm = () => {
  const [childs, setChilds] = useState([createEmptyChild()]);
  const [status, setStatus] = useState("pending");
  const [mode, setMode] = useState("edit");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchChilds();
  }, []);

  const fetchChilds = async () => {
    try {
      const res = await makeRequestAuth("get", "/user/getChild");
      if (res?.data?.childs?.length) {
        setChilds(res.data.childs);
        setStatus(res.data.status || "pending");
        setMode("view");
      } else {
        setChilds([createEmptyChild()]);
        setMode("edit");
      }
    } catch {
      setChilds([createEmptyChild()]);
      setMode("edit");
    }
  };

  const updateChild = (index, field, value) => {
    setChilds(prev =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const addChild = () => setChilds(prev => [...prev, createEmptyChild()]);
  const removeChild = (index) => setChilds(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = childs.map(({ _id, ...rest }) => ({
        ...rest
      }));

      await makeRequestAuth("post", "/user/createOrUpdateChild", { childs: payload });
      await fetchChilds();
      showSuccessToast("Child details saved successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending" },
    approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <CheckCircle size={14} />, label: "Approved" },
    reject: { color: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Rejected" }
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <ToastContainer />

      {/* MODERN HEADER */}
      <div className="bg-[#132b45] rounded-t-3xl p-8 text-white flex justify-between items-end shadow-lg">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <User className="text-blue-400" /> Children Details
          </h1>
          <p className="text-blue-100/60 text-sm mt-1">Information for visa processing and family composition</p>
        </div>

        {mode === "view" && (
          <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider ${STATUS_CONFIG[status]?.color}`}>
            {STATUS_CONFIG[status]?.icon} {STATUS_CONFIG[status]?.label}
          </div>
        )}
      </div>

      <div className="bg-white rounded-b-3xl shadow-xl border border-gray-100 p-8">
        {mode === "view" ? (
          /* ================= VIEW MODE ================= */
          <div className="space-y-6">
            {childs.map((c, i) => (
              <div key={i} className="group border border-gray-100 rounded-2xl p-6 bg-gray-50/50 hover:bg-white hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#132b45] text-white flex items-center justify-center text-xs font-bold">0{i + 1}</div>
                  <h3 className="font-bold text-gray-800">Child Profile</h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <Preview label="First Name" value={c.firstName} />
                  <Preview label="Last Name" value={c.lastName} />
                  <Preview label="Gender" value={c.gender} capitalize />
                  <Preview label="Date of Birth" value={c.dob} isDate />
                  <Preview label="Education" value={c.education} />
                  <Preview label="Current Country" value={c.currentCountry} />
                  <Preview label="Accompanying" value={c.isAccompanying ? "Yes" : "No"} highlight={c.isAccompanying} />
                </div>
              </div>
            ))}

            {status === "reject" && (
              <div className="pt-6 border-t flex justify-center">
                <button
                  onClick={() => setMode("edit")}
                  className="bg-[#132b45] text-white px-10 py-3.5 rounded-2xl font-bold shadow-lg hover:bg-black transition-all active:scale-95"
                >
                  Edit & Re-submit Information
                </button>
              </div>
            )}
          </div>
        ) : (
          /* ================= EDIT MODE ================= */
          <form onSubmit={handleSubmit} className="space-y-8 animate-in fade-in duration-500">
            {childs.map((c, i) => (
              <div key={i} className="relative border-2 border-gray-50 rounded-3xl p-8 bg-white">
                <div className="flex justify-between items-center mb-6">
                  <span className="bg-gray-100 text-gray-500 px-4 py-1 rounded-full text-xs font-bold uppercase">Child {i + 1}</span>
                  {childs.length > 1 && (
                    <button type="button" onClick={() => removeChild(i)} className="text-rose-400 hover:text-rose-600 transition-colors p-2 hover:bg-rose-50 rounded-full">
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Input label="First Name" value={c.firstName} onChange={(v) => updateChild(i, "firstName", v)} required />
                  <Input label="Last Name" value={c.lastName} onChange={(v) => updateChild(i, "lastName", v)} required />

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Date of Birth</label>
                    <input
                      type="date"
                      // Formatting value for standard input
                      value={c.dob ? new Date(c.dob).toISOString().split('T')[0] : ''}
                      onChange={(e) => updateChild(i, "dob", e.target.value)}

                      /* --- VALIDATION ADDED HERE --- */
                      max={new Date().toISOString().split('T')[0]} // Aaj se aage ki date select nahi hogi
                      min="1920-01-01" // Bohot purani dates ko restrict karne ke liye

                      className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all"
                      required
                    />
                    <p className="text-[10px] text-gray-400 mt-1 ml-1">Future dates are not allowed</p>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Gender</label>
                    <select
                      value={c.gender}
                      onChange={(e) => updateChild(i, "gender", e.target.value)}
                      className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all cursor-pointer"
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <Input label="Education (Highest)" value={c.education} onChange={(v) => updateChild(i, "education", v)} />
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">
                      Current Country
                    </label>
                    <select
                      value={c.currentCountry || ""}
                      onChange={(e) => updateChild(i, "currentCountry", e.target.value)}
                      className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all cursor-pointer"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <label className="flex items-center gap-3 mt-4 p-4 rounded-2xl bg-blue-50/50 border border-blue-100 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded-lg border-gray-300 text-[#132b45] focus:ring-[#132b45]"
                      checked={c.isAccompanying}
                      onChange={(e) => updateChild(i, "isAccompanying", e.target.checked)}
                    />
                    <span className="text-sm font-bold text-gray-700">Accompanying to Canada?</span>
                  </label>
                </div>
              </div>
            ))}

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                type="button"
                onClick={addChild}
                className="flex-1 border-2 border-dashed border-gray-200 text-gray-500 font-bold py-4 rounded-2xl hover:border-[#132b45] hover:text-[#132b45] transition-all flex items-center justify-center gap-2"
              >
                <Plus size={20} /> Add Another Child
              </button>

              <button
                type="submit"
                disabled={loading}
                className="flex-[1.5] bg-[#132b45] text-white font-bold py-4 rounded-2xl shadow-lg shadow-blue-900/10 hover:bg-black transition-all flex items-center justify-center"
              >
                {loading ? "Processing..." : "Save All Details"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, required }) => (
  <div>
    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">{label}</label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all"
    />
  </div>
);

const Preview = ({ label, value, isDate, highlight, capitalize }) => (
  <div className="flex flex-col">
    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{label}</span>
    <span className={`font-bold text-gray-800 ${highlight ? 'text-blue-600' : ''} ${capitalize ? 'capitalize' : ''}`}>
      {isDate && value ? new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : (value || "-")}
    </span>
  </div>
);

export default ChildForm;